import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QuickMenu from '../../components/QuickMenu/QuickMenu';
import { getGarbageMapData, getGarbageNeighborhoods } from '../../api/garbageServices';
import { toBlob } from 'html-to-image';
import './GarbageServices.scss';

const LEAFLET_CSS_ID = 'leaflet-css';
const LEAFLET_SCRIPT_ID = 'leaflet-script';
const LEAFLET_HEAT_SCRIPT_ID = 'leaflet-heat-script';

const todayAsInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const yesterdayAsInputValue = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = `${yesterday.getMonth() + 1}`.padStart(2, '0');
  const day = `${yesterday.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ensureLeafletAssets = () => new Promise((resolve, reject) => {
  const loadHeatLayer = () => {
    if (window.L && window.L.heatLayer) {
      resolve(window.L);
      return;
    }
    const heatScript = document.createElement('script');
    heatScript.id = LEAFLET_HEAT_SCRIPT_ID;
    heatScript.src = 'https://cdn.jsdelivr.net/npm/leaflet.heat@0.2.0/dist/leaflet-heat.js';
    heatScript.async = true;
    heatScript.onload = () => resolve(window.L);
    heatScript.onerror = reject;
    document.body.appendChild(heatScript);
  };

  if (window.L && window.L.heatLayer) {
    resolve(window.L);
    return;
  }

  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const link = document.createElement('link');
    link.id = LEAFLET_CSS_ID;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.crossOrigin = '';
    document.head.appendChild(link);
  }

  const existingScript = document.getElementById(LEAFLET_SCRIPT_ID);
  if (existingScript) {
    if (window.L) {
      loadHeatLayer();
    } else {
      existingScript.addEventListener('load', loadHeatLayer);
      existingScript.addEventListener('error', reject);
    }
    return;
  }

  const script = document.createElement('script');
  script.id = LEAFLET_SCRIPT_ID;
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.async = true;
  script.onload = loadHeatLayer;
  script.onerror = reject;
  document.body.appendChild(script);
});

const createBinIcon = (L, isCleaned) => L.icon({
  iconUrl: isCleaned ? '/assets/images/green_bin.png' : '/assets/images/red_bin.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const createTruckIcon = (L) => L.icon({
  iconUrl: '/assets/images/garbage_truck.png',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18]
});

const smoothCoordinates = (coords, iterations = 2) => {
  if (!coords || coords.length < 3) return coords;
  let current = coords;
  for (let iter = 0; iter < iterations; iter++) {
    const next = [];
    next.push(current[0]);
    for (let i = 0; i < current.length - 1; i++) {
      const p0 = current[i];
      const p1 = current[i + 1];
      const q = [
        0.75 * p0[0] + 0.25 * p1[0],
        0.75 * p0[1] + 0.25 * p1[1]
      ];
      const r = [
        0.25 * p0[0] + 0.75 * p1[0],
        0.25 * p0[1] + 0.75 * p1[1]
      ];
      next.push(q);
      next.push(r);
    }
    next.push(current[current.length - 1]);
    current = next;
  }
  return current;
};

const snapCoordinates = (coords, thresholdMeters = 25) => {
  if (!coords || coords.length < 2) return coords;

  const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const dLat = (lat1 - lat2) * 111000;
    const dLon = (lon2 - lon1) * 89000;
    return Math.sqrt(dLat * dLat + dLon * dLon);
  };

  const snapped = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    const pt = coords[i];
    let matchedPoint = pt;

    for (let j = 0; j < snapped.length; j++) {
      const prev = snapped[j];
      if (getDistanceMeters(pt[0], pt[1], prev[0], prev[1]) < thresholdMeters) {
        matchedPoint = prev;
        break;
      }
    }
    snapped.push(matchedPoint);
  }
  return snapped;
};

const getWeightForZoom = (zoom) => {
  if (zoom >= 18) return 18;
  if (zoom === 17) return 14;
  if (zoom === 16) return 10;
  if (zoom === 15) return 7;
  if (zoom === 14) return 5;
  if (zoom === 13) return 3;
  return 2;
};

export default function GarbageServices() {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const leafletLayersRef = useRef([]);
  const fileInputRef = useRef(null);
  const neighborhoodStreetsCacheRef = useRef({});

  const [selectedDate] = useState(todayAsInputValue());
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [selectedUserType, setSelectedUserType] = useState(3); // 3: Container, 4: Sweeper
  const [detailData, setDetailData] = useState(null);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [isNeighborhoodListLoading, setIsNeighborhoodListLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobileLegendOpen, setIsMobileLegendOpen] = useState(false);
  const [sweeperMetrics, setSweeperMetrics] = useState({ sweptPercent: 0, pendingPercent: 0 });

  const activeData = detailData;

  const [reportLocations, setReportLocations] = useState([]);
  const [cleaningDepartment, setCleaningDepartment] = useState(null);
  const [complaintModalData, setComplaintModalData] = useState(null);
  const [complaintForm, setComplaintForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    description: '',
    files: [],
    consentAccepted: false
  });
  const [complaintErrors, setComplaintErrors] = useState({});
  const [isComplaintSubmitting, setIsComplaintSubmitting] = useState(false);
  const [isComplaintSuccess, setIsComplaintSuccess] = useState(false);


  useEffect(() => {
    const loadIncidentOptions = async () => {
      try {
        const rawApiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
        const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`;

        const [depRes, locRes] = await Promise.all([
          fetch(`${API_URL}/ReportIncident/departments`).then(r => r.json()),
          fetch(`${API_URL}/ReportIncident/locations`).then(r => r.json())
        ]);

        if (depRes?.isSuccess && Array.isArray(depRes.data)) {
          const dep = depRes.data.find(d =>
            d.name.toLocaleUpperCase('tr-TR').includes('TEMİZLİK')
          );
          setCleaningDepartment(dep || null);
        }

        if (locRes?.isSuccess && Array.isArray(locRes.data)) {
          setReportLocations(locRes.data);
        }
      } catch (error) {
        console.error('Şikayet seçenekleri yüklenirken hata oluştu:', error);
      }
    };

    loadIncidentOptions();
  }, []);

  const validateComplaintForm = () => {
    const errors = {};
    if (!complaintForm.firstName.trim()) errors.firstName = 'Ad alanı zorunludur.';
    if (!complaintForm.lastName.trim()) errors.lastName = 'Soyad alanı zorunludur.';
    if (!complaintForm.phone.trim()) {
      errors.phone = 'Telefon alanı zorunludur.';
    } else if (!/^0\d{10}$/.test(complaintForm.phone)) {
      errors.phone = 'Telefon numarası 0 ile başlayan 11 rakam olmalıdır.';
    }
    if (!complaintForm.description.trim()) {
      errors.description = 'Şikayet açıklaması zorunludur.';
    } else if (complaintForm.description.trim().length < 10) {
      errors.description = 'Açıklama en az 10 karakter olmalıdır.';
    }
    if (!complaintForm.consentAccepted) {
      errors.consentAccepted = 'Onay gereklidir.';
    }
    setComplaintErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    if (!validateComplaintForm() || isComplaintSubmitting) return;

    const matchedLoc = reportLocations.find(l =>
      l.name.toLocaleUpperCase('tr-TR').replace(/\s+MAHALLESİ$/, '').trim() ===
      complaintModalData.neighborhood.toLocaleUpperCase('tr-TR').replace(/\s+MAHALLESİ$/, '').trim()
    ) || reportLocations[0];

    if (!matchedLoc) {
      alert('Şikayet kaydı oluşturulamadı: İlgili mahalle sistemde tanımlı değil.');
      return;
    }

    if (!cleaningDepartment) {
      alert('Şikayet kaydı oluşturulamadı: Temizlik İşleri Müdürlüğü birimi sistemde tanımlı değil.');
      return;
    }

    const payload = new FormData();
    payload.append('FirstName', complaintForm.firstName.trim());
    payload.append('LastName', complaintForm.lastName.trim());
    payload.append('Phone', complaintForm.phone.trim());
    payload.append('NeighborhoodGuid', matchedLoc.guid);
    payload.append('NeighborhoodName', matchedLoc.name);

    let addressDetailText = '';
    const googleMapsUrl = `https://www.google.com/maps?q=${complaintModalData.latitude.toFixed(6)},${complaintModalData.longitude.toFixed(6)}`;
    if (complaintModalData.isSweeper) {
      const prefix = selectedUserType === 18 ? '[İlaçlama Şikayeti]' : selectedUserType === 9 ? '[Ravo Şikayeti]' : '[Süpürge Şikayeti]';
      addressDetailText = `${prefix} Konum: ${complaintModalData.latitude.toFixed(6)}, ${complaintModalData.longitude.toFixed(6)}, Durum: ${complaintModalData.statusText}, Harita: ${googleMapsUrl}`;
    } else {
      const binLabel = complaintModalData.binName || complaintModalData.binId || 'Konteyner';
      addressDetailText = `[Konteyner Şikayeti] No: ${binLabel}, Adres: ${complaintModalData.binAddress || ''}, Konum: ${complaintModalData.latitude.toFixed(6)}, ${complaintModalData.longitude.toFixed(6)}, Durum: ${complaintModalData.statusText || 'Bilinmiyor'}, Temizlik Zamanı: ${complaintModalData.lastVisitTime || 'Bilinmiyor'}, Harita: ${googleMapsUrl}`;
    }
    payload.append('AddressDetail', addressDetailText.trim());
    payload.append('EventLocation', `${matchedLoc.name} ${addressDetailText}`.trim());

    payload.append('EventDescription', complaintForm.description.trim());
    payload.append('DepartmentGuid', cleaningDepartment.guid);
    payload.append('DepartmentName', cleaningDepartment.name);
    payload.append('ConsentAccepted', String(complaintForm.consentAccepted));

    complaintForm.files.forEach((file) => payload.append('files', file));

    setIsComplaintSubmitting(true);
    try {
      const rawApiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
      const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`;

      const response = await fetch(`${API_URL}/ReportIncident`, {
        method: 'POST',
        body: payload
      });

      const resText = await response.text();
      let resJson;
      try {
        resJson = JSON.parse(resText);
      } catch {
        throw new Error('API yanıtı çözümlenemedi.');
      }

      if (!response.ok || !resJson?.isSuccess) {
        throw new Error(resJson?.resultMessage || 'Şikayet gönderilemedi.');
      }

      setIsComplaintSuccess(true);
      setComplaintForm({
        firstName: '',
        lastName: '',
        phone: '',
        description: '',
        files: [],
        consentAccepted: false
      });
    } catch (error) {
      alert(`Şikayet iletilirken hata oluştu: ${error.message}`);
    } finally {
      setIsComplaintSubmitting(false);
    }
  };

  const selectedNeighborhoodOption = useMemo(
    () => neighborhoodOptions.find((item) => item.name === selectedNeighborhood) || null,
    [neighborhoodOptions, selectedNeighborhood]
  );

  const fetchNeighborhoodOptions = useCallback(async () => {
    setIsNeighborhoodListLoading(true);
    setIsPreviewLoading(true);
    setErrorMessage('');

    try {
      const queryDate = selectedDate;
      const data = await getGarbageNeighborhoods({ date: queryDate, selectedUserType });
      setNeighborhoodOptions(data?.neighborhoods || []);
    } catch (error) {
      setNeighborhoodOptions([]);
      setIsPreviewLoading(false);
      setErrorMessage(
        error?.response?.data?.message ||
        'Mahalle listesi alınamadı.'
      );
    } finally {
      setIsNeighborhoodListLoading(false);
    }
  }, [selectedDate, selectedUserType]);

  const fetchNeighborhoodDetail = useCallback(async (neighborhoodName) => {
    if (!neighborhoodName) {
      setSelectedNeighborhood('');
      setDetailData(null);
      return;
    }

    const option = neighborhoodOptions.find((item) => item.name === neighborhoodName);
    if (!option && neighborhoodName !== 'TÜM MAHALLELER') {
      return;
    }

    setIsDetailLoading(true);
    setSelectedNeighborhood(neighborhoodName);
    setErrorMessage('');

    try {
      const queryDate = selectedDate;
      const data = await getGarbageMapData({
        zoneId: neighborhoodName === 'TÜM MAHALLELER' ? 0 : option.zoneId,
        date: queryDate,
        selectedUserType,
        neighborhoodName
      });

      setDetailData(data);
      if (neighborhoodName !== 'TÜM MAHALLELER') {
        setNeighborhoodOptions((currentOptions) => currentOptions.map((item) => {
          if (item.name === neighborhoodName) {
            const total = data?.totalBins ?? 0;
            const cleaned = data?.cleanedBins ?? 0;
            const statusColor = selectedUserType === 4
              ? (data?.statusColor ?? item.statusColor)
              : (total > 0 && (cleaned / total) >= 0.70 ? 'green' : cleaned > 0 ? 'yellow' : 'red');
            return {
              ...item,
              totalBins: total,
              cleanedBins: cleaned,
              pendingBins: data?.pendingBins ?? 0,
              statusColor
            };
          }
          return item;
        }));
      }
    } catch (error) {
      setDetailData(null);
      setErrorMessage(
        error?.response?.data?.message ||
        'Mahalle detay verisi alınamadı.'
      );
    } finally {
      setIsDetailLoading(false);
    }
  }, [neighborhoodOptions, selectedDate, selectedUserType]);

  useEffect(() => {
    setSelectedNeighborhood('');
    setDetailData(null);
  }, [selectedUserType]);

  useEffect(() => {
    fetchNeighborhoodOptions();
  }, [fetchNeighborhoodOptions]);

  useEffect(() => {
    if (!neighborhoodOptions.length || selectedNeighborhood || isDetailLoading) {
      return;
    }

    const defaultNeighborhood = neighborhoodOptions.find((item) => {
      const normalizedName = item.name
        .toLocaleUpperCase('tr-TR')
        .replace(/\s+MAHALLESİ$/, '')
        .trim();

      return normalizedName === 'AKARCA';
    });

    if (defaultNeighborhood) {
      fetchNeighborhoodDetail(defaultNeighborhood.name)
        .finally(() => setIsPreviewLoading(false));
    } else {
      setIsPreviewLoading(false);
    }
  }, [fetchNeighborhoodDetail, isDetailLoading, neighborhoodOptions, selectedNeighborhood]);


  useEffect(() => {
    let isMounted = true;

    const initializeMap = async () => {
      if (!mapRef.current || !activeData) {
        return;
      }

      let sweeperTotal = 0;
      let sweeperSwept = 0;

      const L = await ensureLeafletAssets();
      if (!isMounted || !mapRef.current) {
        return;
      }

      if (!leafletMapRef.current) {
        leafletMapRef.current = L.map(mapRef.current, {
          zoomControl: true,
          minZoom: 11
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap katkıcıları',
          crossOrigin: true
        }).addTo(leafletMapRef.current);

        leafletMapRef.current.on('popupopen', (e) => {
          const container = e.popup.getElement();
          if (!container) return;
          const btn = container.querySelector('.garbage-report-btn');
          if (btn) {
            btn.onclick = (event) => {
              event.preventDefault();
              const binId = btn.getAttribute('data-bin-id');
              const binName = btn.getAttribute('data-bin-name');
              const binAddress = btn.getAttribute('data-bin-address');
              const neighborhood = btn.getAttribute('data-neighborhood');
              const lat = parseFloat(btn.getAttribute('data-lat'));
              const lon = parseFloat(btn.getAttribute('data-lon'));
              const statusText = btn.getAttribute('data-status');
              const lastVisitTime = btn.getAttribute('data-visit-time');

              setComplaintModalData({
                binId,
                binName,
                binAddress,
                neighborhood,
                latitude: lat,
                longitude: lon,
                statusText,
                lastVisitTime,
                isSweeper: false
              });
              leafletMapRef.current.closePopup();
            };
          }

          const sweeperBtn = container.querySelector('.sweeper-report-btn');
          if (sweeperBtn) {
            sweeperBtn.onclick = async (event) => {
              event.preventDefault();
              const lat = parseFloat(sweeperBtn.getAttribute('data-lat'));
              const lon = parseFloat(sweeperBtn.getAttribute('data-lon'));
              const statusText = sweeperBtn.getAttribute('data-status');
              const neighborhood = sweeperBtn.getAttribute('data-neighborhood');

              leafletMapRef.current.closePopup();

              setComplaintModalData({
                isSweeper: true,
                latitude: lat,
                longitude: lon,
                statusText,
                neighborhood,
                screenshotFile: null,
                isScreenshotLoading: true
              });

              setComplaintForm((prev) => ({
                ...prev,
                files: []
              }));

              // Run screenshot capture asynchronously in the background so the form modal opens instantly
              (async () => {
                try {
                  // 1. Temporarily inline CSS styles onto the SVG path elements.
                  // Serialized SVG data URIs used by html-to-image ignore external/scoped page stylesheets.
                  const svgPane = mapRef.current.querySelector('.leaflet-overlay-pane svg');
                  const originalPathStyles = [];

                  if (svgPane) {
                    const paths = Array.from(svgPane.querySelectorAll('path'));
                    paths.forEach((p) => {
                      const isRed = p.classList.contains('glowing-street-red');
                      const isGreen = p.classList.contains('glowing-street-green');
                      const isYellow = p.classList.contains('glowing-street-yellow');

                      if (isRed || isGreen || isYellow) {
                        originalPathStyles.push({
                          element: p,
                          filter: p.style.filter,
                          opacity: p.style.opacity,
                          strokeOpacity: p.style.strokeOpacity,
                          stroke: p.style.stroke
                        });

                        // Force drop-shadow inline so html-to-image serializes it correctly
                        const shadowColor = isGreen ? '#22c55e' : isYellow ? '#eab308' : '#ef4444';
                        p.style.filter = `drop-shadow(0 0 4px ${shadowColor})`;
                        p.style.strokeOpacity = '1';
                        p.style.opacity = '0.95';
                        p.style.stroke = shadowColor;
                      }
                    });
                  }

                  // 2. Instantly hide any active leaflet popups so they don't appear in the screenshot (even mid-fadeout)
                  const popups = Array.from(mapRef.current.querySelectorAll('.leaflet-popup'));
                  const originalPopupDisplays = popups.map((popup) => {
                    const originalDisplay = popup.style.display;
                    popup.style.display = 'none';
                    return { popup, originalDisplay };
                  });

                  // Small delay to allow DOM style updates to settle
                  await new Promise((resolve) => setTimeout(resolve, 50));

                  const rawBlob = await toBlob(mapRef.current, {
                    cacheBust: true,
                  });

                  // ── Draw a red pin on the screenshot at the clicked lat/lon ──
                  let blob = rawBlob;
                  try {
                    const lmap = leafletMapRef.current;
                    if (lmap && rawBlob) {
                      const mapRect = mapRef.current.getBoundingClientRect();
                      const point = lmap.latLngToContainerPoint([lat, lon]);
                      const img = await createImageBitmap(rawBlob);
                      const scaleX = img.width / mapRect.width;
                      const scaleY = img.height / mapRect.height;
                      const px = point.x * scaleX;
                      const py = point.y * scaleY;

                      const canvas = document.createElement('canvas');
                      canvas.width = img.width;
                      canvas.height = img.height;
                      const ctx = canvas.getContext('2d');
                      ctx.drawImage(img, 0, 0);

                      // Draw pin drop shadow
                      const pinW = 28 * scaleX;
                      const pinH = 36 * scaleY;
                      ctx.shadowColor = 'rgba(0,0,0,0.45)';
                      ctx.shadowBlur = 6 * scaleX;
                      ctx.shadowOffsetY = 3 * scaleY;

                      // Pin body (teardrop)
                      ctx.fillStyle = '#dc2626';
                      ctx.beginPath();
                      ctx.arc(px, py - pinH + pinW / 2, pinW / 2, Math.PI, 0);
                      ctx.quadraticCurveTo(px + pinW / 2, py - pinH / 2, px, py);
                      ctx.quadraticCurveTo(px - pinW / 2, py - pinH / 2, px - pinW / 2, py - pinH + pinW / 2);
                      ctx.closePath();
                      ctx.fill();

                      // White inner circle
                      ctx.shadowColor = 'transparent';
                      ctx.fillStyle = '#ffffff';
                      ctx.beginPath();
                      ctx.arc(px, py - pinH + pinW / 2, pinW * 0.22, 0, Math.PI * 2);
                      ctx.fill();

                      blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
                    }
                  } catch (pinErr) {
                    console.warn('Pin overlay failed, using raw screenshot:', pinErr);
                  }

                  // Restore path styles
                  originalPathStyles.forEach((item) => {
                    item.element.style.filter = item.filter;
                    item.element.style.opacity = item.opacity;
                    item.element.style.strokeOpacity = item.strokeOpacity;
                    item.element.style.stroke = item.stroke;
                  });

                  // Restore popups
                  originalPopupDisplays.forEach((item) => {
                    item.popup.style.display = item.originalDisplay;
                  });

                  const screenshotFile = new File(
                    [blob],
                    `harita_konum_${lat.toFixed(6)}_${lon.toFixed(6)}.png`,
                    { type: 'image/png' }
                  );

                  // Update modal data to attach screenshot and mark loading as finished
                  setComplaintModalData((prev) => {
                    if (!prev) return null; // user might have closed the modal already
                    return {
                      ...prev,
                      screenshotFile,
                      isScreenshotLoading: false
                    };
                  });

                  setComplaintForm((prev) => ({
                    ...prev,
                    files: [screenshotFile]
                  }));
                } catch (err) {
                  console.error('Failed to capture map screenshot in background:', err);
                  setComplaintModalData((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      isScreenshotLoading: false
                    };
                  });
                }
              })();
            };
          }
        });

        const updateBlurForZoom = (zoom) => {
          let blurVal = '5px';
          if (zoom === 15) blurVal = '3px';
          else if (zoom === 14) blurVal = '1.5px';
          else if (zoom <= 13) blurVal = '0px';

          if (mapRef.current) {
            mapRef.current.style.setProperty('--street-blur', blurVal);
          }
        };

        updateBlurForZoom(leafletMapRef.current.getZoom());

        leafletMapRef.current.on('zoomend', () => {
          const currentZoom = leafletMapRef.current.getZoom();
          const targetWeight = getWeightForZoom(currentZoom);

          updateBlurForZoom(currentZoom);

          leafletLayersRef.current.forEach((layer) => {
            if (layer.options && layer.options.isRouteLine) {
              layer.setStyle({ weight: targetWeight });
            }
          });
        });
      }

      leafletLayersRef.current.forEach((layer) => layer.remove());
      leafletLayersRef.current = [];

      const bounds = [];
      const cleanedIcon = createBinIcon(L, true);
      const pendingIcon = createBinIcon(L, false);
      const truckIcon = createTruckIcon(L);

      // Draw neighborhood boundaries if coordinates are available
      if (activeData.boundaryCoordinates && activeData.boundaryCoordinates.length > 0) {
        const polygon = L.polygon(activeData.boundaryCoordinates, {
          color: 'rgb(8, 73, 119)',
          fill: false,
          weight: 2,
          dashArray: '5, 5'
        }).addTo(leafletMapRef.current);

        leafletLayersRef.current.push(polygon);
        activeData.boundaryCoordinates.forEach((coord) => {
          bounds.push(coord);
        });
      }

      if (selectedUserType === 4 || selectedUserType === 18 || selectedUserType === 9) {
        const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
          const dLat = (lat1 - lat2) * 111000;
          const dLon = (lon2 - lon1) * 89000;
          return Math.sqrt(dLat * dLat + dLon * dLon);
        };

        const sweeperPoints = [];
        if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
          activeData.sweeperPaths.forEach((path) => {
            if (!path.coordinates) return;
            path.coordinates.forEach((coord) => {
              sweeperPoints.push({
                lat: coord[0],
                lon: coord[1],
                color: path.color
              });
            });
          });
        }

        if (selectedNeighborhood === 'TÜM MAHALLELER') {
          // ── Load all neighborhood street files in parallel ────────────────
          const neighborhoodNames = (activeData.neighborhoods || []).map((n) => n.name);
          const toSlug = (name) => name
            .toLocaleUpperCase('tr-TR')
            .replace(/\s+MAHALLESİ$/, '').replace(/\s+MAH\.$/, '')
            .trim().toLowerCase()
            .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
            .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
            .replace(/\s+/g, '_');

          const slugs = neighborhoodNames.map(toSlug);
          const missing = slugs.filter((s) => !neighborhoodStreetsCacheRef.current[s]);
          await Promise.all(missing.map(async (slug) => {
            try {
              const res = await fetch(`/data/streets/${slug}.json`);
              neighborhoodStreetsCacheRef.current[slug] = res.ok ? await res.json() : [];
            } catch { neighborhoodStreetsCacheRef.current[slug] = []; }
          }));
          const allStreets = slugs.flatMap((s) => neighborhoodStreetsCacheRef.current[s] || []);

          // ── Build spatial grid for O(1) sweeper point lookup ─────────────
          // Grid cell ≈ 44m, so 35m threshold is always covered by ±1 cell search
          const CELL = 0.0004;
          const swGrid = {};
          sweeperPoints.forEach((pt) => {
            const key = `${Math.floor(pt.lat / CELL)},${Math.floor(pt.lon / CELL)}`;
            if (!swGrid[key]) swGrid[key] = [];
            swGrid[key].push(pt);
          });
          const nearbyPts = (lat, lon) => {
            const gx = Math.floor(lat / CELL);
            const gy = Math.floor(lon / CELL);
            const out = [];
            for (let dx = -1; dx <= 1; dx++)
              for (let dy = -1; dy <= 1; dy++) {
                const cell = swGrid[`${gx + dx},${gy + dy}`];
                if (cell) for (let i = 0; i < cell.length; i++) out.push(cell[i]);
              }
            return out;
          };

          // ── Build spatial grid for street coords (off-road detection) ─────
          const stGrid = {};
          allStreets.forEach((street) => {
            if (!street.geometry) return;
            street.geometry.forEach((pt) => {
              const key = `${Math.floor(pt.lat / CELL)},${Math.floor(pt.lon / CELL)}`;
              if (!stGrid[key]) stGrid[key] = [];
              stGrid[key].push(pt);
            });
          });
          const isNearStreet = (lat, lon) => {
            const gx = Math.floor(lat / CELL);
            const gy = Math.floor(lon / CELL);
            for (let dx = -1; dx <= 1; dx++)
              for (let dy = -1; dy <= 1; dy++) {
                const cell = stGrid[`${gx + dx},${gy + dy}`];
                if (cell) for (let i = 0; i < cell.length; i++) {
                  if (getDistanceMeters(lat, lon, cell[i].lat, cell[i].lon) < 35) return true;
                }
              }
            return false;
          };

          // ── Match streets against sweeper grid ────────────────────────────
          const redLatlngsAll = [], greenLatlngsAll = [], yellowLatlngsAll = [];
          allStreets.forEach((street) => {
            if (!street.geometry || street.geometry.length < 2) return;
            const latlngs = street.geometry.map((pt) => [pt.lat, pt.lon]);
            let hasGreen = false, hasYellow = false;
            for (let i = 0; i < street.geometry.length; i++) {
              const sPt = street.geometry[i];
              const candidates = nearbyPts(sPt.lat, sPt.lon);
              for (let j = 0; j < candidates.length; j++) {
                const swPt = candidates[j];
                const dist = getDistanceMeters(sPt.lat, sPt.lon, swPt.lat, swPt.lon);
                if (dist < 35) {
                  if (swPt.color === 'green') { hasGreen = true; break; }
                  else if (swPt.color === 'yellow') hasYellow = true;
                }
              }
              if (hasGreen) break;
            }
            if (hasGreen) greenLatlngsAll.push(latlngs);
            else if (hasYellow) yellowLatlngsAll.push(latlngs);
            else redLatlngsAll.push(latlngs);
          });

          // ── Off-road segments: sweeper coords not on any street ───────────
          if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
            activeData.sweeperPaths.forEach((path) => {
              if (!path.coordinates || path.coordinates.length === 0) return;
              let seg = [];
              path.coordinates.forEach((coord) => {
                const lat = coord[0], lon = coord[1];
                if (!isNearStreet(lat, lon)) {
                  seg.push([lat, lon]);
                } else {
                  if (seg.length >= 2) {
                    if (path.color === 'green') greenLatlngsAll.push(seg);
                    else yellowLatlngsAll.push(seg);
                  }
                  seg = [];
                }
              });
              if (seg.length >= 2) {
                if (path.color === 'green') greenLatlngsAll.push(seg);
                else yellowLatlngsAll.push(seg);
              }
            });
          }

          // ── Add bounds ────────────────────────────────────────────────────
          sweeperPoints.forEach((pt) => bounds.push([pt.lat, pt.lon]));

          const initialWeight = getWeightForZoom(leafletMapRef.current.getZoom());

          // ── Draw 3 multi-polylines (single Leaflet layer per color) ───────
          if (redLatlngsAll.length > 0) {
            leafletLayersRef.current.push(
              L.polyline(redLatlngsAll, { color: '#ef4444', weight: initialWeight, opacity: 0.75, className: 'glowing-street-red', lineCap: 'round', lineJoin: 'round', interactive: false, isRouteLine: true }).addTo(leafletMapRef.current)
            );
          }
          if (greenLatlngsAll.length > 0) {
            leafletLayersRef.current.push(
              L.polyline(greenLatlngsAll, { color: '#22c55e', weight: initialWeight, opacity: 0.85, className: 'glowing-street-green', lineCap: 'round', lineJoin: 'round', interactive: false, isRouteLine: true }).addTo(leafletMapRef.current)
            );
          }
          if (yellowLatlngsAll.length > 0) {
            leafletLayersRef.current.push(
              L.polyline(yellowLatlngsAll, { color: '#eab308', weight: initialWeight, opacity: 0.85, className: 'glowing-street-yellow', lineCap: 'round', lineJoin: 'round', interactive: false, isRouteLine: true }).addTo(leafletMapRef.current)
            );
          }

          // ── Invisible interaction layers (clickable) ─────────────────────
          const addInteractionLayer = (latlngsArr, streetColor) => {
            latlngsArr.forEach((segLatlngs) => {
              if (!segLatlngs || segLatlngs.length < 2) return;
              const interactionLine = L.polyline(segLatlngs, {
                color: 'transparent',
                weight: 20,
                opacity: 0,
                className: 'street-interaction-layer',
                interactive: true
              }).addTo(leafletMapRef.current);
              leafletLayersRef.current.push(interactionLine);

              interactionLine.on('click', (e) => {
                const clickedLatLng = e.latlng;
                const cleanStatusText = selectedUserType === 18
                  ? (streetColor === '#22c55e' ? 'Son 2 Saat İçinde İlaçlandı' : streetColor === '#eab308' ? '2+ Saat Önce İlaçlandı' : 'Hiç İlaçlanmadı')
                  : selectedUserType === 9
                    ? (streetColor === '#22c55e' ? 'Son 2 Saat İçinde Yıkandı' : streetColor === '#eab308' ? '2+ Saat Önce Yıkandı' : 'Yıkanmadı')
                    : (streetColor === '#22c55e' ? 'Son 2 Saat İçinde Süpürüldü' : streetColor === '#eab308' ? '2+ Saat Önce Süpürüldü' : 'Hiç Süpürülmedi');

                const popupContent = `
                  <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                    <span>Durum: <span style="font-weight:bold;color:${streetColor};">${cleanStatusText}</span></span>
                    <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                      <button type="button" class="sweeper-report-btn" 
                        data-lat="${clickedLatLng.lat}" 
                        data-lon="${clickedLatLng.lng}"
                        data-status="${cleanStatusText}"
                        data-neighborhood="TÜM MAHALLELER"
                        style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                        Şikayet Bildir
                      </button>
                    </div>
                  </div>
                `;

                L.popup()
                  .setLatLng(clickedLatLng)
                  .setContent(popupContent)
                  .openOn(leafletMapRef.current);
              });
            });
          };

          addInteractionLayer(redLatlngsAll, '#ef4444');
          addInteractionLayer(greenLatlngsAll, '#22c55e');
          addInteractionLayer(yellowLatlngsAll, '#eab308');

          sweeperTotal = redLatlngsAll.length + greenLatlngsAll.length + yellowLatlngsAll.length;
          sweeperSwept = greenLatlngsAll.length + yellowLatlngsAll.length;
        } else if (selectedNeighborhood) {


          const slug = selectedNeighborhood
            .toLocaleUpperCase('tr-TR')
            .replace(/\s+MAHALLESİ$/, '')
            .replace(/\s+MAH\.$/, '')
            .trim()
            .toLowerCase()
            .replace(/ç/g, 'c')
            .replace(/ğ/g, 'g')
            .replace(/ı/g, 'i')
            .replace(/ö/g, 'o')
            .replace(/ş/g, 's')
            .replace(/ü/g, 'u')
            .replace(/\s+/g, '_');

          if (!neighborhoodStreetsCacheRef.current[slug]) {
            try {
              const res = await fetch(`/data/streets/${slug}.json`);
              if (res.ok) {
                neighborhoodStreetsCacheRef.current[slug] = await res.json();
              } else {
                neighborhoodStreetsCacheRef.current[slug] = [];
              }
            } catch (err) {
              console.error(`Failed to load streets for ${selectedNeighborhood}:`, err);
              neighborhoodStreetsCacheRef.current[slug] = [];
            }
          }

          const streets = neighborhoodStreetsCacheRef.current[slug] || [];
          const redLatlngs = [];
          const greenLatlngs = [];
          const yellowLatlngs = [];

          const flatStreetCoords = [];
          streets.forEach((street) => {
            if (street.geometry) {
              street.geometry.forEach((pt) => {
                flatStreetCoords.push(pt);
              });
            }
          });

          streets.forEach((street) => {
            if (!street.geometry || street.geometry.length < 2) return;
            const latlngs = street.geometry.map((pt) => [pt.lat, pt.lon]);

            let streetColor = '#ef4444'; // default red = never swept
            let hasMatchedGreen = false;
            let hasMatchedYellow = false;

            for (let i = 0; i < street.geometry.length; i++) {
              const sPt = street.geometry[i];
              for (let j = 0; j < sweeperPoints.length; j++) {
                const swPt = sweeperPoints[j];
                const dist = getDistanceMeters(sPt.lat, sPt.lon, swPt.lat, swPt.lon);
                if (dist < 35) { // 35 meters threshold
                  if (swPt.color === 'green') {
                    hasMatchedGreen = true;
                    break;
                  } else if (swPt.color === 'yellow') {
                    hasMatchedYellow = true;
                  }
                }
              }
              if (hasMatchedGreen) break;
            }

            if (hasMatchedGreen) {
              streetColor = '#22c55e'; // green
              greenLatlngs.push(latlngs);
            } else if (hasMatchedYellow) {
              streetColor = '#eab308'; // yellow (swept but 2+ hours ago, incl. 6+h)
              yellowLatlngs.push(latlngs);
            } else {
              streetColor = '#ef4444'; // red = never visited by sweeper
              redLatlngs.push(latlngs);
            }

            // Draw interaction layer (invisible but clickable)
            const polyline = L.polyline(latlngs, {
              color: 'transparent',
              weight: 16,
              opacity: 0,
              className: 'street-interaction-layer',
              interactive: true
            }).addTo(leafletMapRef.current);

            leafletLayersRef.current.push(polyline);

            polyline.on('click', (e) => {
              const clickedLatLng = e.latlng;
              const cleanStatusText = selectedUserType === 18
                ? (streetColor === '#22c55e' ? 'Son 2 Saat İçinde İlaçlandı' : streetColor === '#eab308' ? '2+ Saat Önce İlaçlandı' : 'Hiç İlaçlanmadı')
                : selectedUserType === 9
                  ? (streetColor === '#22c55e' ? 'Son 2 Saat İçinde Yıkandı' : streetColor === '#eab308' ? '2+ Saat Önce Yıkandı' : 'Yıkanmadı')
                  : (streetColor === '#22c55e' ? 'Son 2 Saat İçinde Süpürüldü' : streetColor === '#eab308' ? '2+ Saat Önce Süpürüldü' : 'Hiç Süpürülmedi');

              const popupContent = `
                <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                  <span>Durum: <span style="font-weight:bold;color:${streetColor};">${cleanStatusText}</span></span>
                  <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                    <button type="button" class="sweeper-report-btn" 
                      data-lat="${clickedLatLng.lat}" 
                      data-lon="${clickedLatLng.lng}"
                      data-status="${cleanStatusText}"
                      data-neighborhood="${selectedNeighborhood}"
                      style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                      Şikayet Bildir
                    </button>
                  </div>
                </div>
              `;

              L.popup()
                .setLatLng(clickedLatLng)
                .setContent(popupContent)
                .openOn(leafletMapRef.current);
            });
          });

          // Extract off-road segments from sweeper paths and render them as green/yellow glowing paths
          if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
            activeData.sweeperPaths.forEach((path) => {
              if (!path.coordinates || path.coordinates.length === 0) return;

              let currentOffRoadSegment = [];

              path.coordinates.forEach((coord) => {
                const lat = coord[0];
                const lon = coord[1];
                const close = flatStreetCoords.some(
                  (pt) => getDistanceMeters(lat, lon, pt.lat, pt.lon) < 35
                );

                if (!close) {
                  currentOffRoadSegment.push([lat, lon]);
                } else {
                  if (currentOffRoadSegment.length >= 2) {
                    const segColor = path.color === 'green' ? '#22c55e' : '#eab308';
                    const segClass = path.color === 'green' ? 'glowing-street-green' : 'glowing-street-yellow';
                    if (path.color === 'green') greenLatlngs.push(currentOffRoadSegment);
                    else yellowLatlngs.push(currentOffRoadSegment);
                    currentOffRoadSegment.forEach((ll) => bounds.push(ll));

                    // Visible colored polyline for off-road segment
                    const visPolyline = L.polyline(currentOffRoadSegment, {
                      color: segColor,
                      weight: getWeightForZoom(leafletMapRef.current.getZoom()),
                      opacity: 0.85,
                      className: segClass,
                      lineCap: 'round',
                      lineJoin: 'round',
                      interactive: false,
                      isRouteLine: true
                    }).addTo(leafletMapRef.current);
                    leafletLayersRef.current.push(visPolyline);

                    const polyline = L.polyline(currentOffRoadSegment, {
                      color: 'transparent',
                      weight: 16,
                      opacity: 0,
                      className: 'street-interaction-layer',
                      interactive: true
                    }).addTo(leafletMapRef.current);
                    leafletLayersRef.current.push(polyline);

                    polyline.on('click', (e) => {
                      const clickedLatLng = e.latlng;
                      const cleanStatusText = path.color === 'green'
                        ? (selectedUserType === 18 ? 'Son 2 Saat İçinde İlaçlandı' : selectedUserType === 9 ? 'Son 2 Saat İçinde Yıkandı' : 'Son 2 Saat İçinde Süpürüldü')
                        : (selectedUserType === 18 ? '2+ Saat Önce İlaçlandı' : selectedUserType === 9 ? '2+ Saat Önce Yıkandı' : '2+ Saat Önce Süpürüldü');
                      const statusColor = path.color === 'green' ? '#22c55e' : '#eab308';
                      const popupContent = `
                        <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                          <span>Durum: <span style="font-weight:bold;color:${statusColor};">${cleanStatusText}</span></span>
                          <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                            <button type="button" class="sweeper-report-btn" 
                              data-lat="${clickedLatLng.lat}" 
                              data-lon="${clickedLatLng.lng}"
                              data-status="${cleanStatusText}"
                              data-neighborhood="${selectedNeighborhood}"
                              style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                              Şikayet Bildir
                            </button>
                          </div>
                        </div>
                      `;
                      L.popup().setLatLng(clickedLatLng).setContent(popupContent).openOn(leafletMapRef.current);
                    });
                  }
                  currentOffRoadSegment = [];
                }
              });

              if (currentOffRoadSegment.length >= 2) {
                const segColor = path.color === 'green' ? '#22c55e' : '#eab308';
                const segClass = path.color === 'green' ? 'glowing-street-green' : 'glowing-street-yellow';
                if (path.color === 'green') greenLatlngs.push(currentOffRoadSegment);
                else yellowLatlngs.push(currentOffRoadSegment);
                currentOffRoadSegment.forEach((ll) => bounds.push(ll));

                // Visible colored polyline for trailing off-road segment
                const visPolyline = L.polyline(currentOffRoadSegment, {
                  color: segColor,
                  weight: getWeightForZoom(leafletMapRef.current.getZoom()),
                  opacity: 0.85,
                  className: segClass,
                  lineCap: 'round',
                  lineJoin: 'round',
                  interactive: false,
                  isRouteLine: true
                }).addTo(leafletMapRef.current);
                leafletLayersRef.current.push(visPolyline);

                const polyline = L.polyline(currentOffRoadSegment, {
                  color: 'transparent',
                  weight: 16,
                  opacity: 0,
                  className: 'street-interaction-layer',
                  interactive: true
                }).addTo(leafletMapRef.current);
                leafletLayersRef.current.push(polyline);

                polyline.on('click', (e) => {
                  const clickedLatLng = e.latlng;
                  const cleanStatusText = path.color === 'green'
                    ? (selectedUserType === 18 ? 'Son 2 Saat İçinde İlaçlandı' : selectedUserType === 9 ? 'Son 2 Saat İçinde Yıkandı' : 'Son 2 Saat İçinde Süpürüldü')
                    : (selectedUserType === 18 ? '2+ Saat Önce İlaçlandı' : selectedUserType === 9 ? '2+ Saat Önce Yıkandı' : '2+ Saat Önce Süpürüldü');
                  const statusColor = path.color === 'green' ? '#22c55e' : '#eab308';
                  const popupContent = `
                    <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                      <span>Durum: <span style="font-weight:bold;color:${statusColor};">${cleanStatusText}</span></span>
                      <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                        <button type="button" class="sweeper-report-btn" 
                          data-lat="${clickedLatLng.lat}" 
                          data-lon="${clickedLatLng.lng}"
                          data-status="${cleanStatusText}"
                          data-neighborhood="${selectedNeighborhood}"
                          style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                          Şikayet Bildir
                        </button>
                      </div>
                    </div>
                  `;
                  L.popup().setLatLng(clickedLatLng).setContent(popupContent).openOn(leafletMapRef.current);
                });
              }
            });
          }


          const initialWeight = getWeightForZoom(leafletMapRef.current.getZoom());

          if (greenLatlngs.length > 0) {
            const greenGlow = L.polyline(greenLatlngs, {
              color: '#22c55e',
              weight: initialWeight,
              opacity: 0.85,
              className: 'glowing-street-green',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false,
              isRouteLine: true
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(greenGlow);
          }

          if (yellowLatlngs.length > 0) {
            const yellowGlow = L.polyline(yellowLatlngs, {
              color: '#eab308',
              weight: initialWeight,
              opacity: 0.85,
              className: 'glowing-street-yellow',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false,
              isRouteLine: true
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(yellowGlow);
          }

          if (redLatlngs.length > 0) {
            const redGlow = L.polyline(redLatlngs, {
              color: '#ef4444',
              weight: initialWeight,
              opacity: 0.75,
              className: 'glowing-street-red',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false,
              isRouteLine: true
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(redGlow);
          }

          sweeperTotal = redLatlngs.length + greenLatlngs.length + yellowLatlngs.length;
          sweeperSwept = greenLatlngs.length + yellowLatlngs.length;
        }

        // Draw dynamic rotated vehicle icons (top-down view) at their absolute last known coordinates
        if (activeData.activeVehicles && activeData.activeVehicles.length > 0) {
          activeData.activeVehicles.forEach((vehicle) => {
            if (typeof vehicle.latitude !== 'number' || typeof vehicle.longitude !== 'number') return;
            const lastLatLng = [vehicle.latitude, vehicle.longitude];
            const rotationAngle = vehicle.angle || 0;

            const matchingPath = activeData.sweeperPaths?.find((p) => p.userId === vehicle.userId);
            const pathColor = matchingPath ? (matchingPath.color === 'green' ? '#22c55e' : '#eab308') : (selectedUserType === 18 ? '#0ea5e9' : selectedUserType === 9 ? '#f97316' : '#facc15');
            let vehicleIconHtml = '';

            if (selectedUserType === 18) {
              // Spraying pickup truck top-down view SVG
              vehicleIconHtml = `
                <div class="spraying-truck-wrapper" style="transform: rotate(${rotationAngle}deg); transform-origin: center; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px;">
                  <svg viewBox="0 0 64 64" width="40" height="40">
                    <!-- Side mirrors -->
                    <rect x="12" y="18" width="6" height="3" rx="1" fill="#1e293b"/>
                    <rect x="46" y="18" width="6" height="3" rx="1" fill="#1e293b"/>
                    
                    <!-- Truck Body (Chassis + Bed) -->
                    <rect x="16" y="10" width="32" height="48" rx="4" fill="#f8fafc" stroke="#475569" stroke-width="2"/>
                    
                    <!-- Front Cab -->
                    <rect x="18" y="14" width="28" height="18" rx="2" fill="${pathColor}"/>
                    <!-- Windshield -->
                    <path d="M20 18h24v3H20z" fill="#93c5fd"/>
                    <!-- Front Hood -->
                    <rect x="22" y="10" width="20" height="4" fill="#94a3b8"/>

                    <!-- Pickup Bed (Back part) -->
                    <rect x="18" y="34" width="28" height="22" rx="1" fill="#475569"/>
                    
                    <!-- Spraying Tank in the Bed -->
                    <rect x="20" y="36" width="24" height="14" rx="7" fill="#0ea5e9"/>
                    <!-- Tank details -->
                    <circle cx="32" cy="43" r="3" fill="#0284c7"/>
                    <rect x="24" y="50" width="16" height="3" rx="1" fill="#0284c7"/>
                    
                    <!-- Spray nozzle lines coming out of back -->
                    <line x1="22" y1="53" x2="14" y2="58" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
                    <line x1="42" y1="53" x2="50" y2="58" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
              `;
            } else if (selectedUserType === 9) {
              // Ravo street washing vehicle top-down view SVG
              vehicleIconHtml = `
                <div class="ravo-truck-wrapper" style="transform: rotate(${rotationAngle}deg); transform-origin: center; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px;">
                  <svg viewBox="0 0 64 64" width="44" height="44">
                    <!-- Side mirrors -->
                    <rect x="10" y="20" width="7" height="4" rx="1" fill="#1e293b"/>
                    <rect x="47" y="20" width="7" height="4" rx="1" fill="#1e293b"/>

                    <!-- Main Ravo Body (wider/heavier) -->
                    <rect x="14" y="10" width="36" height="48" rx="5" fill="#f97316" stroke="#c2410c" stroke-width="2"/>

                    <!-- Front Cab -->
                    <rect x="16" y="14" width="32" height="16" rx="2" fill="${pathColor !== '#f97316' ? pathColor : '#ea580c'}"/>
                    <!-- Windshield -->
                    <path d="M18 17h28v4H18z" fill="#93c5fd" opacity="0.85"/>

                    <!-- Water tank (center of truck) -->
                    <rect x="17" y="32" width="30" height="18" rx="3" fill="#0369a1"/>
                    <rect x="20" y="35" width="24" height="12" rx="2" fill="#0284c7"/>
                    <circle cx="32" cy="41" r="4" fill="#0ea5e9"/>

                    <!-- Water spray jets at front bottom -->
                    <line x1="18" y1="56" x2="12" y2="62" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round"/>
                    <line x1="26" y1="57" x2="24" y2="63" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
                    <line x1="38" y1="57" x2="40" y2="63" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
                    <line x1="46" y1="56" x2="52" y2="62" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round"/>

                    <!-- Brushes at front -->
                    <ellipse cx="20" cy="12" rx="5" ry="3" fill="#1e293b"/>
                    <ellipse cx="44" cy="12" rx="5" ry="3" fill="#1e293b"/>
                  </svg>
                </div>
              `;
            } else {
              // Sweeper walking personnel — simple solid circle pin
              vehicleIconHtml = `
                <div class="sweeper-personnel-wrapper" style="width: 18px; height: 18px; background: ${pathColor}; border: 3px solid #fff; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.28);"></div>
              `;
            }

            const vehicleDivIcon = L.divIcon({
              className: selectedUserType === 18 ? 'spraying-truck-leaflet-div-icon' : selectedUserType === 9 ? 'ravo-truck-leaflet-div-icon' : 'sweeper-personnel-leaflet-div-icon',
              html: vehicleIconHtml,
              iconSize: selectedUserType === 4 ? [24, 24] : [44, 44],
              iconAnchor: selectedUserType === 4 ? [12, 12] : [22, 22],
              popupAnchor: [0, -14]
            });

            const vehicleMarker = L.marker(lastLatLng, {
              icon: vehicleDivIcon
            })
              .bindPopup(`
                <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                  <strong>${selectedUserType === 18 ? 'İlaçlama Pikabı' : selectedUserType === 9 ? 'Ravo Aracı' : 'Süpürge Personeli'}</strong>
                  <div style="margin-top:6px;border-top:1px solid #e2e8f0;padding-top:6px;font-size:0.8rem;color:#475569;">
                    <span>Personel Cihaz No: <strong>${vehicle.userId || 'Bilinmeyen'}</strong></span><br/>
                    <span>Son Görülme: <strong>${vehicle.lastVisitTime || ''}</strong></span>
                  </div>
                </div>
              `)
              .addTo(leafletMapRef.current);
            leafletLayersRef.current.push(vehicleMarker);

            if (selectedNeighborhood === 'TÜM MAHALLELER') {
              bounds.push(lastLatLng);
            }
          });
        }

        const sweptPercent = sweeperTotal > 0 ? Math.round((sweeperSwept / sweeperTotal) * 100) : 0;
        const pendingPercent = 100 - sweptPercent;
        setSweeperMetrics({ sweptPercent, pendingPercent });
      } else {
        // Draw container vehicle routes if available
        if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
          const vehicleColorMap = {};
          const routeColors = [
            '#06b6d4', // Turquoise / Cyan
            '#059669', // Emerald Green
            '#2563eb', // Royal Blue
            '#0d9488', // Teal
            '#0284c7', // Ocean Blue
            '#10b981', // Mint Green
            '#4f46e5', // Indigo Blue
            '#475569'  // Slate Gray
          ];
          let colorIndex = 0;

          activeData.sweeperPaths.forEach((path) => {
            if (!path.coordinates || path.coordinates.length < 2) return;
            const rawLatlngs = path.coordinates.map((coord) => [coord[0], coord[1]]);

            // Assign a consistent color to each vehicle
            const vehicleId = path.userId || 'default';
            if (!vehicleColorMap[vehicleId]) {
              vehicleColorMap[vehicleId] = routeColors[colorIndex % routeColors.length];
              colorIndex++;
            }
            const pathColor = vehicleColorMap[vehicleId];

            // Snap overlapping parallel coordinates to previous points to merge them perfectly into a single line
            const snappedLatlngs = snapCoordinates(rawLatlngs, 25);
            const smoothedSeg = smoothCoordinates(snappedLatlngs, 2);

            // Draw single thick solid route line (no glow) with zoom-dependent thickness
            const routePolyline = L.polyline(smoothedSeg, {
              color: pathColor, // Use mapped color per vehicle
              weight: getWeightForZoom(leafletMapRef.current.getZoom()),
              opacity: 0.85,
              lineCap: 'round',
              lineJoin: 'round',
              smoothFactor: 0,
              interactive: false,
              isRouteLine: true
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(routePolyline);

            rawLatlngs.forEach((ll) => bounds.push(ll));

            // Draw dynamic rotated garbage truck vector icon (top-down view) at the actual final coordinate of the route
            const lastLatLng = rawLatlngs[rawLatlngs.length - 1];
            let rotationAngle = 0;
            if (rawLatlngs.length >= 2) {
              const prevLatLng = rawLatlngs[rawLatlngs.length - 2];
              const dy = lastLatLng[0] - prevLatLng[0];
              const dx = (lastLatLng[1] - prevLatLng[1]) * Math.cos(prevLatLng[0] * Math.PI / 180);
              const angle = Math.atan2(dx, dy) * 180 / Math.PI;
              rotationAngle = angle; // Since top-down SVG naturally points North (up), no offset is needed
            }

            const truckIconHtml = `
              <div class="garbage-truck-wrapper" style="transform: rotate(${rotationAngle}deg); transform-origin: center; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px;">
                <svg viewBox="0 0 64 64" width="40" height="40">
                  <!-- Side mirrors -->
                  <rect x="12" y="14" width="6" height="3" rx="1.5" fill="#1e293b"/>
                  <rect x="46" y="14" width="6" height="3" rx="1.5" fill="#1e293b"/>
                  
                  <!-- Main Truck Body (Cargo Container) -->
                  <rect x="18" y="18" width="28" height="38" rx="2" fill="#0284c7"/>
                  <!-- Garbage hopper back slope details -->
                  <rect x="20" y="52" width="24" height="4" fill="#0369a1"/>
                  <rect x="18" y="22" width="28" height="2" fill="#ffffff" opacity="0.2"/>
                  <rect x="18" y="34" width="28" height="2" fill="#ffffff" opacity="0.2"/>
                  <rect x="18" y="46" width="28" height="2" fill="#ffffff" opacity="0.2"/>

                  <!-- Cab (Front of the truck) - Color matches the route color -->
                  <rect x="20" y="8" width="24" height="11" rx="2" fill="${pathColor}"/>
                  <!-- Windshield -->
                  <path d="M22 11h20v2H22z" fill="#93c5fd"/>
                  <!-- Hood details -->
                  <rect x="26" y="9" width="12" height="1" fill="#1e3a8a"/>
                </svg>
              </div>
            `;

            const truckDivIcon = L.divIcon({
              className: 'garbage-truck-leaflet-div-icon',
              html: truckIconHtml,
              iconSize: [40, 40],
              iconAnchor: [20, 20],
              popupAnchor: [0, -20]
            });

            const truckMarker = L.marker(lastLatLng, {
              icon: truckDivIcon
            })
              .bindPopup(`<strong>Çöp Toplama Aracı</strong>`)
              .addTo(leafletMapRef.current);
            leafletLayersRef.current.push(truckMarker);
          });
        }

        // Draw container bins
        (activeData.bins || []).forEach((bin) => {
          if (typeof bin.latitude !== 'number' || typeof bin.longitude !== 'number') {
            return;
          }

          const marker = L.marker([bin.latitude, bin.longitude], {
            icon: bin.isCleaned ? cleanedIcon : pendingIcon
          })
            .bindPopup(`
              <div style="min-width:220px">
                <strong>Konteyner numarası: ${bin.name || bin.id || 'Konteyner'}</strong><br/>
                <span>Durum: ${bin.statusText}</span>
                ${bin.isCleaned && bin.lastVisitTime ? `<br/><span>Temizlenme saati: ${bin.lastVisitTime}</span>` : ''}
                <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                  <button type="button" class="garbage-report-btn" 
                    data-bin-id="${bin.id || ''}" 
                    data-bin-name="${bin.name || ''}" 
                    data-bin-address="${bin.address || ''}" 
                    data-neighborhood="${bin.neighborhoodName || ''}"
                    data-lat="${bin.latitude || ''}"
                    data-lon="${bin.longitude || ''}"
                    data-status="${bin.statusText || ''}"
                    data-visit-time="${bin.lastVisitTime || ''}"
                    style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                    Şikayet Bildir
                  </button>
                </div>
              </div>
            `)
            .addTo(leafletMapRef.current);

          leafletLayersRef.current.push(marker);
          bounds.push([bin.latitude, bin.longitude]);
        });
      }

      if (bounds.length === 1) {
        leafletMapRef.current.setView(bounds[0], 17);
      } else if (bounds.length > 1) {
        leafletMapRef.current.fitBounds(bounds, { padding: [36, 36] });
      } else {
        leafletMapRef.current.setView([36.5872, 36.1734], 13);
      }
    };

    initializeMap().catch(() => {
      if (isMounted) {
        setErrorMessage('Harita bileşeni yüklenemedi.');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [activeData]);

  useEffect(() => () => {
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }
  }, []);

  return (
    <div className="garbage-services-page">
      <div className="container-fluid">
        <div className="garbage-services-shell">
          {errorMessage ? (
            <div className="garbage-services-feedback is-error">{errorMessage}</div>
          ) : null}

          <aside className="garbage-services-quick-menu">

            {/* Harita Seçimi - Sol Sidebar En Üst */}
            <div className="garbage-services-side-card" style={{ marginBottom: '1rem', padding: '12px' }}>

              {/* Harita Seçim Yönlendirmesi */}
              <div style={{ marginBottom: '10px', padding: '10px 12px', background: '#f1f5f9', borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '0.76rem', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>Harita Seçimi</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: '1.45' }}>Aşağıdaki menüden görmek istediğiniz hizmet haritasını seçebilirsiniz.</div>
                </div>
              </div>

              <div className="garbage-services-mode-tabs" style={{ display: 'flex', flexDirection: 'column', background: '#f1f5f9', padding: '6px', borderRadius: '14px', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => setSelectedUserType(3)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: selectedUserType === 3 ? '#fff' : 'transparent',
                    color: selectedUserType === 3 ? '#084977' : '#64748b',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: selectedUserType === 3 ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                >
                  Konteyner Haritası
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedUserType(4)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: selectedUserType === 4 ? '#fff' : 'transparent',
                    color: selectedUserType === 4 ? '#084977' : '#64748b',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: selectedUserType === 4 ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                >
                  Süpürge Haritası
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedUserType(18)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: selectedUserType === 18 ? '#fff' : 'transparent',
                    color: selectedUserType === 18 ? '#084977' : '#64748b',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: selectedUserType === 18 ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                >
                  İlaçlama Haritası
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedUserType(9)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: selectedUserType === 9 ? '#fff' : 'transparent',
                    color: selectedUserType === 9 ? '#084977' : '#64748b',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    boxShadow: selectedUserType === 9 ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                >
                  Ravo Süpürge Haritası
                </button>
              </div>
            </div>

            {/* Süpürge Haritası Masaüstü Bilgilendirme Kartı */}
            {selectedUserType === 4 && (
              <div className="garbage-sweeper-info-card is-sidebar-visible">
                <div className="info-card-header">
                  <span className="info-card-title">Sokak Temizlik Durumu</span>
                </div>
                <div className="info-card-body">
                  <div className="info-item">
                    <span className="dot dot-green" />
                    <div className="info-content">
                      <span className="label">Yakın Zamanda Temizlendi</span>
                      <span className="desc">Son 2 saat içerisinde süpürülen sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-yellow" />
                    <div className="info-content">
                      <span className="label">Gün İçinde Temizlendi</span>
                      <span className="desc">2 saatten daha uzun süre önce süpürülen sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-red" />
                    <div className="info-content">
                      <span className="label">Temizlik Programında</span>
                      <span className="desc">Henüz süpürülmemiş, gün içinde temizlenecek sokaklar.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* İlaçlama Haritası Masaüstü Bilgilendirme Kartı */}
            {selectedUserType === 18 && (
              <div className="garbage-sweeper-info-card is-sidebar-visible">
                <div className="info-card-header">
                  <span className="info-card-title">Sokak İlaçlama Durumu</span>
                </div>
                <div className="info-card-body">
                  <div className="info-item">
                    <span className="dot dot-green" />
                    <div className="info-content">
                      <span className="label">Yakın Zamanda İlaçlandı</span>
                      <span className="desc">Son 2 saat içerisinde ilaçlanan sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-yellow" />
                    <div className="info-content">
                      <span className="label">Gün İçinde İlaçlandı</span>
                      <span className="desc">2 saatten daha uzun süre önce ilaçlanan sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-red" />
                    <div className="info-content">
                      <span className="label">İlaçlama Programında</span>
                      <span className="desc">Henüz ilaçlanmamış, gün içinde ilaçlanacak sokaklar.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ravo Haritası Masaüstü Bilgilendirme Kartı */}
            {selectedUserType === 9 && (
              <div className="garbage-sweeper-info-card is-sidebar-visible">
                <div className="info-card-header">
                  <span className="info-card-title">Ravo Araç Durumu</span>
                </div>
                <div className="info-card-body">
                  <div className="info-item">
                    <span className="dot dot-green" />
                    <div className="info-content">
                      <span className="label">Yakın Zamanda Yıkandı</span>
                      <span className="desc">Son 2 saat içerisinde ravo aracı geçmiş sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-yellow" />
                    <div className="info-content">
                      <span className="label">Gün İçinde Yıkandı</span>
                      <span className="desc">2 saatten daha uzun süre önce ravo geçmiş sokaklar.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-red" />
                    <div className="info-content">
                      <span className="label">Yıkama Programında</span>
                      <span className="desc">Henüz yıkanmamış, gün içinde yıkanacak sokaklar.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Temizlik Hizmetleri Konteyner Haritası Masaüstü Bilgilendirme Kartı */}
            {selectedUserType === 3 && (
              <div className="garbage-sweeper-info-card is-sidebar-visible">
                <div className="info-card-header">
                  <span className="info-card-title">Konteyner Durumu</span>
                </div>
                <div className="info-card-body">
                  <div className="info-item">
                    <span className="dot dot-green" />
                    <div className="info-content">
                      <span className="label">Boşaltıldı / Temizlendi</span>
                      <span className="desc">Gün içinde boşaltılmış ve temizlenmiş konteynerler.</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="dot dot-red" />
                    <div className="info-content">
                      <span className="label">Boşaltılacak</span>
                      <span className="desc">Temizlik programında olan, boşaltılacak konteynerler.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </aside>

          <div className="garbage-services-content">
            <div className="garbage-services-map-card">
              <div className="garbage-services-map-header" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'stretch' }}>
                <div className="garbage-services-map-header-row">
                  <div>
                    <h2 className="garbage-services-section-title">
                      {selectedUserType === 4 ? 'Süpürge Haritası' : selectedUserType === 18 ? 'İlaçlama Haritası' : selectedUserType === 9 ? 'Ravo Süpürge Haritası' : 'Konteyner Haritası'}
                    </h2>
                    <div className="garbage-services-helper">
                      {selectedNeighborhood
                        ? selectedNeighborhood === 'TÜM MAHALLELER'
                          ? `Tüm mahallelerin ${selectedUserType === 4 ? 'süpürge temizlik rotaları' : selectedUserType === 18 ? 'ilaçlama rotaları' : selectedUserType === 9 ? 'ravo yıkama rotaları' : 'konteyner verileri'} gösteriliyor.`
                          : `${selectedNeighborhood} Mahallesi ${selectedUserType === 4 ? 'süpürge temizlik rotaları' : selectedUserType === 18 ? 'ilaçlama rotaları' : selectedUserType === 9 ? 'ravo yıkama rotaları' : 'konteyner verileri'} gösteriliyor.`
                        : 'Mahalle verisi hazırlanıyor.'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div className="garbage-services-mobile-neighborhood">
                    <label htmlFor="garbage-neighborhood-mobile">Mahalle</label>
                    <select
                      id="garbage-neighborhood-mobile"
                      value={selectedNeighborhood}
                      onChange={(event) => fetchNeighborhoodDetail(event.target.value)}
                      disabled={!neighborhoodOptions.length || isDetailLoading}
                    >
                      <option value="">Mahalle seçiniz</option>
                      <option value="TÜM MAHALLELER">Tüm Mahalleler</option>
                      {neighborhoodOptions.map((option) => (
                        <option key={`mobile-${option.zoneId}-${option.name}`} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedUserType === 4 || selectedUserType === 18 || selectedUserType === 9 ? (
                    <div className="garbage-services-map-meta">
                      <span className="garbage-services-chip is-cleaned">
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                        {selectedUserType === 18 ? 'İlaçlandı' : selectedUserType === 9 ? 'Yıkandı' : 'Temizlendi'}: %{sweeperMetrics.sweptPercent}
                      </span>
                      <span className="garbage-services-chip is-pending">
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                        {selectedUserType === 18 ? 'İlaçlanacak' : selectedUserType === 9 ? 'Yıkanmadı' : 'Temizlenecek'}: %{sweeperMetrics.pendingPercent}
                      </span>
                    </div>
                  ) : (
                    <div className="garbage-services-map-meta">
                      <span className="garbage-services-chip is-cleaned">
                        <img src="/assets/images/green_bin.png" alt="Temizlendi" className="garbage-chip-icon" />
                        Temizlendi: {selectedNeighborhoodOption?.cleanedBins ?? activeData?.cleanedBins ?? 0}
                      </span>
                      <span className="garbage-services-chip is-pending">
                        <img src="/assets/images/red_bin.png" alt="Temizlenecek" className="garbage-chip-icon" />
                        Temizlenecek: {selectedNeighborhoodOption?.pendingBins ?? activeData?.pendingBins ?? 0}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="garbage-services-map-shell">
                <div ref={mapRef} className="garbage-services-map" />
                {isDetailLoading || isPreviewLoading ? (
                  <div className="garbage-services-map-loading">
                    <div className="garbage-services-map-spinner" />
                    <span>Harita güncelleniyor...</span>
                  </div>
                ) : null}

                {/* Süpürge Haritası Vatandaş Bilgilendirme Kartı / Mobil Toggle */}
                {selectedUserType === 4 && !isDetailLoading && !isPreviewLoading && (
                  <>
                    {!isMobileLegendOpen ? (
                      <button
                        type="button"
                        className="garbage-mobile-legend-toggle"
                        onClick={() => setIsMobileLegendOpen(true)}
                      >
                        <span className="info-circle-icon">i</span> Renk Anlamları
                      </button>
                    ) : (
                      <div className="garbage-sweeper-info-card is-mobile-visible">
                        <div className="info-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="info-card-title">Sokak Temizlik Durumu</span>
                          <button
                            type="button"
                            className="legend-close-btn"
                            onClick={() => setIsMobileLegendOpen(false)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '1.25rem',
                              cursor: 'pointer',
                              padding: '0 4px',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                            }}
                          >
                            &times;
                          </button>
                        </div>
                        <div className="info-card-body">
                          <div className="info-item">
                            <span className="dot dot-green" />
                            <div className="info-content">
                              <span className="label">Yakın Zamanda Temizlendi</span>
                              <span className="desc">Son 2 saat içerisinde süpürme işlemi yapılmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-yellow" />
                            <div className="info-content">
                              <span className="label">Gün İçinde Temizlendi</span>
                              <span className="desc">2 saatten daha uzun süre önce temizlik işlemi yapılmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-red" />
                            <div className="info-content">
                              <span className="label">Temizlik Programında</span>
                              <span className="desc">Bugün henüz süpürme sırası gelmemiş, gün içinde temizlenecek sokaklar.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* İlaçlama Haritası Vatandaş Bilgilendirme Kartı / Mobil Toggle */}
                {selectedUserType === 18 && !isDetailLoading && !isPreviewLoading && (
                  <>
                    {!isMobileLegendOpen ? (
                      <button
                        type="button"
                        className="garbage-mobile-legend-toggle"
                        onClick={() => setIsMobileLegendOpen(true)}
                      >
                        <span className="info-circle-icon">i</span> Renk Anlamları
                      </button>
                    ) : (
                      <div className="garbage-sweeper-info-card is-mobile-visible">
                        <div className="info-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="info-card-title">Sokak İlaçlama Durumu</span>
                          <button
                            type="button"
                            className="legend-close-btn"
                            onClick={() => setIsMobileLegendOpen(false)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '1.25rem',
                              cursor: 'pointer',
                              padding: '0 4px',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                            }}
                          >
                            &times;
                          </button>
                        </div>
                        <div className="info-card-body">
                          <div className="info-item">
                            <span className="dot dot-green" />
                            <div className="info-content">
                              <span className="label">Yakın Zamanda İlaçlandı</span>
                              <span className="desc">Son 2 saat içerisinde ilaçlama işlemi yapılmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-yellow" />
                            <div className="info-content">
                              <span className="label">Gün İçinde İlaçlandı</span>
                              <span className="desc">2 saatten daha uzun süre önce ilaçlama işlemi yapılmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-red" />
                            <div className="info-content">
                              <span className="label">İlaçlama Programında</span>
                              <span className="desc">Bugün henüz ilaçlama sırası gelmemiş, gün içinde ilaçlanacak sokaklar.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Ravo Haritası Vatandaş Bilgilendirme Kartı / Mobil Toggle */}
                {selectedUserType === 9 && !isDetailLoading && !isPreviewLoading && (
                  <>
                    {!isMobileLegendOpen ? (
                      <button
                        type="button"
                        className="garbage-mobile-legend-toggle"
                        onClick={() => setIsMobileLegendOpen(true)}
                      >
                        <span className="info-circle-icon">i</span> Renk Anlamları
                      </button>
                    ) : (
                      <div className="garbage-sweeper-info-card is-mobile-visible">
                        <div className="info-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="info-card-title">Ravo Araç Durumu</span>
                          <button
                            type="button"
                            className="legend-close-btn"
                            onClick={() => setIsMobileLegendOpen(false)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '1.25rem',
                              cursor: 'pointer',
                              padding: '0 4px',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                            }}
                          >
                            &times;
                          </button>
                        </div>
                        <div className="info-card-body">
                          <div className="info-item">
                            <span className="dot dot-green" />
                            <div className="info-content">
                              <span className="label">Yakın Zamanda Yıkandı</span>
                              <span className="desc">Son 2 saat içerisinde ravo aracı tarafından yıkanmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-yellow" />
                            <div className="info-content">
                              <span className="label">Gün İçinde Yıkandı</span>
                              <span className="desc">2 saatten daha uzun süre önce yıkanmış sokaklar.</span>
                            </div>
                          </div>
                          <div className="info-item">
                            <span className="dot dot-red" />
                            <div className="info-content">
                              <span className="label">Yıkama Programında</span>
                              <span className="desc">Bugün henüz yıkanmamış, gün içinde yıkanacak sokaklar.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Mahalle Seçimi - Sağ Sidebar */}
            <div className="garbage-services-side">
              <div className="garbage-services-side-card garbage-services-neighborhood-card">
                <div className="garbage-services-side-header">
                  <h3 className="garbage-services-section-title">Mahalle Seçimi</h3>
                  <span className="garbage-services-side-count">{neighborhoodOptions.length} kayıt</span>
                </div>
                {isNeighborhoodListLoading ? (
                  <div className="garbage-services-helper">Mahalle listesi hazırlanıyor...</div>
                ) : null}
                <div className="garbage-services-list">
                  <button
                    type="button"
                    className={`garbage-services-neighborhood-button${selectedNeighborhood === 'TÜM MAHALLELER' ? ' is-active' : ''}`}
                    onClick={() => fetchNeighborhoodDetail('TÜM MAHALLELER')}
                    disabled={isDetailLoading}
                    style={{ marginBottom: '2px' }}
                  >
                    <div className="garbage-services-neighborhood-title" style={{ fontWeight: '800' }}>
                      Tüm Mahalleler
                    </div>
                    <div className="garbage-services-neighborhood-meta">
                      Bütün bölgedeki rotaları ve verileri gör.
                    </div>
                  </button>

                  {neighborhoodOptions.length ? neighborhoodOptions.map((item) => (
                    <button
                      key={`${item.zoneId}-${item.name}`}
                      type="button"
                      className={`garbage-services-neighborhood-button${selectedNeighborhood === item.name ? ' is-active' : ''}`}
                      onClick={() => fetchNeighborhoodDetail(item.name)}
                      disabled={isDetailLoading}
                    >
                      <div className="garbage-services-neighborhood-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.statusColor && (
                          <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            display: 'inline-block',
                            flexShrink: 0,
                            background: item.statusColor === 'green' ? '#22c55e' : item.statusColor === 'yellow' ? '#eab308' : '#ef4444'
                          }} />
                        )}
                        {item.name}
                      </div>
                    </button>
                  )) : (
                    <div className="garbage-services-feedback">
                      {isNeighborhoodListLoading ? 'Yükleniyor...' : 'Uygun mahalle kaydı bulunamadı.'}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      {complaintModalData && (
        <div className="garbage-modal-overlay">
          <div className="garbage-modal-container">
            <div className="garbage-modal-header">
              <h3 className="garbage-modal-title">
                {complaintModalData.isSweeper ? (selectedUserType === 18 ? 'İlaçlama Şikayet Bildirimi' : selectedUserType === 9 ? 'Ravo Şikayet Bildirimi' : 'Temizlik Şikayet Bildirimi') : 'Konteyner Şikayet Bildirimi'}
              </h3>
              <button
                type="button"
                className="garbage-modal-close"
                onClick={() => {
                  setComplaintModalData(null);
                  setIsComplaintSuccess(false);
                  setComplaintErrors({});
                }}
              >
                &times;
              </button>
            </div>

            <div className="garbage-modal-body">
              {isComplaintSuccess ? (
                <div className="garbage-modal-success">
                  Şikayet bildiriminiz başarıyla Temizlik İşleri Müdürlüğü'ne iletilmiştir.
                </div>
              ) : (
                <form onSubmit={handleComplaintSubmit}>
                  <div className="garbage-modal-info">
                    {complaintModalData.isSweeper ? (
                      <>
                        <strong>Durum:</strong> {complaintModalData.statusText}<br />
                        <strong>Konum:</strong>{' '}
                        <a
                          href={`https://www.google.com/maps?q=${complaintModalData.latitude.toFixed(6)},${complaintModalData.longitude.toFixed(6)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
                        >
                          {complaintModalData.latitude.toFixed(6)}, {complaintModalData.longitude.toFixed(6)}
                        </a>
                        <br />
                        <strong>Mahalle:</strong> {complaintModalData.neighborhood} Mahallesi<br />
                      </>
                    ) : (
                      <>
                        <strong>Konteyner:</strong> {complaintModalData.binName || complaintModalData.binId || 'Konteyner'}<br />
                        <strong>Mahalle:</strong> {complaintModalData.neighborhood} Mahallesi<br />
                        {complaintModalData.binAddress && (
                          <div><strong>Adres:</strong> {complaintModalData.binAddress}</div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="garbage-modal-grid">
                    <div className="garbage-modal-group">
                      <label>Ad *</label>
                      <input
                        type="text"
                        value={complaintForm.firstName}
                        onChange={(e) => setComplaintForm(p => ({ ...p, firstName: e.target.value }))}
                        className={complaintErrors.firstName ? 'error' : ''}
                      />
                      {complaintErrors.firstName && <span className="error-text">{complaintErrors.firstName}</span>}
                    </div>
                    <div className="garbage-modal-group">
                      <label>Soyad *</label>
                      <input
                        type="text"
                        value={complaintForm.lastName}
                        onChange={(e) => setComplaintForm(p => ({ ...p, lastName: e.target.value }))}
                        className={complaintErrors.lastName ? 'error' : ''}
                      />
                      {complaintErrors.lastName && <span className="error-text">{complaintErrors.lastName}</span>}
                    </div>
                  </div>

                  <div className="garbage-modal-group">
                    <label>Telefon *</label>
                    <input
                      type="text"
                      value={complaintForm.phone}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        const withoutLeading = digits.replace(/^0+/, '');
                        setComplaintForm(p => ({ ...p, phone: `0${withoutLeading}`.slice(0, 11) }));
                      }}
                      onFocus={() => !complaintForm.phone && setComplaintForm(p => ({ ...p, phone: '0' }))}
                      maxLength={11}
                      placeholder="05xxxxxxxxx"
                      className={complaintErrors.phone ? 'error' : ''}
                    />
                    {complaintErrors.phone && <span className="error-text">{complaintErrors.phone}</span>}
                  </div>

                  <div className="garbage-modal-group">
                    <label>Şikayet Açıklaması *</label>
                    <textarea
                      rows={3}
                      value={complaintForm.description}
                      onChange={(e) => setComplaintForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Lütfen şikayetinizi detaylı şekilde açıklayınız."
                      className={complaintErrors.description ? 'error' : ''}
                    />
                    {complaintErrors.description && <span className="error-text">{complaintErrors.description}</span>}
                  </div>

                  <div className="garbage-modal-group">
                    <label>Fotoğraf / Video (İsteğe Bağlı) <small style={{ color: '#64748b', fontWeight: 'normal' }}>(En fazla 5 dosya, her biri max 25 MB)</small></label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const filesArr = Array.from(e.target.files || []);
                        const validFiles = filesArr.filter(f => f.size <= 25 * 1024 * 1024);
                        if (filesArr.length !== validFiles.length) {
                          alert('25 MB üzerindeki dosyalar elendi.');
                        }
                        setComplaintForm(p => ({ ...p, files: [...p.files, ...validFiles].slice(0, 5) }));
                        e.target.value = '';
                      }}
                    />
                    <div
                      className="garbage-file-drop-zone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#084977', marginBottom: '8px' }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Dosyaları seçmek için tıklayın</span>
                    </div>

                    {(complaintForm.files.length > 0 || complaintModalData.isScreenshotLoading) && (
                      <div className="garbage-modal-files">
                        {complaintModalData.isScreenshotLoading && (
                          <div className="file-item loading" style={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg className="animate-spin" style={{ width: '16px', height: '16px', animation: 'garbage-spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,0.1)" strokeWidth="4" />
                              <path d="M4 12a8 8 0 0 1 8-8" stroke="#084977" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Harita ekran görüntüsü hazırlanıyor...</span>
                          </div>
                        )}
                        {complaintForm.files.map((file, idx) => (
                          <div key={idx} className="file-item">
                            <span title={file.name}>{file.name}</span>
                            <small style={{ color: '#64748b', margin: '0 8px' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</small>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setComplaintForm(p => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));
                              }}
                            >
                              Kaldır
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <label className={`consent-box ${complaintErrors.consentAccepted ? 'error' : ''}`}>
                    <input
                      type="checkbox"
                      checked={complaintForm.consentAccepted}
                      onChange={(e) => setComplaintForm(p => ({ ...p, consentAccepted: e.target.checked }))}
                    />
                    <span>
                      Geri dönüş yapılabilmesi için bilgilerimin kullanılmasını kabul ediyorum.
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="garbage-modal-submit"
                    disabled={isComplaintSubmitting || complaintModalData.isScreenshotLoading}
                  >
                    {isComplaintSubmitting
                      ? 'Gönderiliyor...'
                      : complaintModalData.isScreenshotLoading
                        ? 'Ekran Görüntüsü Hazırlanıyor...'
                        : 'Şikayeti İlet'
                    }
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
